import { PopoverProps, popoverClasses } from '@mui/material'
import { Theme } from '@mui/material/styles'

// ----------------------------------------------------------------------
// Anything that pops over the page: menus, dropdowns, the kebab menu. It takes
// the card radius, the pop shadow (e2) and a hairline edge in the divider
// colour. Flat first: the shadow says it floats, the hairline says where it
// ends, and on a dark ground the hairline is the one doing the work.
//
// The hairline is a 1px ring in the shadow list rather than a `border`. The
// two draw identically, but a ring is part of the elevation signature: a
// screen that makes a popover chromeless (`boxShadow: 'none'`, as the
// onboarding checklist does around its own card) loses the edge with the
// shadow instead of being left with a rectangle drawn around nothing. A
// border would also add 2px to the paper and move every arrow that is
// positioned against its edge.
// ----------------------------------------------------------------------

/** The e2 shadow with the hairline edge in front of it. */
export const popSurfaceShadow = (theme: Theme) =>
  `0 0 0 1px ${theme.palette.divider}, ${theme.customShadows.dropdown}`

// ----------------------------------------------------------------------
// What a control opens stands off it. A menu, a select's list, an
// autocomplete's list or a date picker's calendar that opens under, over or
// beside the control that opened it leaves one spacing step between them, so
// the two never touch (asked for on 2026-11-02: Export's menu opened flush
// under its button, which on a phone read as the button stuck to the menu).
// It is read from where the paper meets its control, so no screen states it:
//
// - a popover (every Menu, Select and Popover) from the two origins it was
//   given: under its control (`bottom` to `top`), over it (`top` to
//   `bottom`), or beside it (`right` to `left`, and the mirror);
// - an autocomplete's list and a picker's calendar from the side Popper put
//   them on, which flips to the top when there is no room below.
//
// A menu laid over its control, or placed at a point (`anchorPosition`),
// meets no edge and takes none. A menu the top nav opens on hover stays
// flush: it closes as the pointer leaves the item, so a gap would drop it on
// the way there.
// ----------------------------------------------------------------------

/** The room between a control and what it opens, in spacing steps. */
const ANCHOR_GAP = 1

/** Whether the paper is held open by the pointer resting on it. */
const heldByPointer = ({ PaperProps, slotProps }: PopoverProps) => {
  const paper =
    typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper
  return typeof (paper ?? PaperProps)?.onMouseLeave === 'function'
}

/**
 * The margin that moves a popover's paper the gap off the edge it shares with
 * its control, or nothing where it shares none.
 */
export const popoverGap = (theme: Theme, ownerState: PopoverProps) => {
  const {
    anchorReference = 'anchorEl',
    anchorOrigin,
    transformOrigin,
  } = ownerState
  if (
    anchorReference !== 'anchorEl' ||
    !anchorOrigin ||
    !transformOrigin ||
    heldByPointer(ownerState)
  ) {
    return {}
  }

  const off = theme.spacing(ANCHOR_GAP)
  const back = theme.spacing(-ANCHOR_GAP)
  if (
    anchorOrigin.vertical === 'bottom' &&
    transformOrigin.vertical === 'top'
  ) {
    return { marginTop: off }
  }
  if (
    anchorOrigin.vertical === 'top' &&
    transformOrigin.vertical === 'bottom'
  ) {
    return { marginTop: back }
  }
  if (
    anchorOrigin.horizontal === 'right' &&
    transformOrigin.horizontal === 'left'
  ) {
    return { marginLeft: off }
  }
  if (
    anchorOrigin.horizontal === 'left' &&
    transformOrigin.horizontal === 'right'
  ) {
    return { marginLeft: back }
  }
  return {}
}

/**
 * The same gap for a surface Popper places, read from the side it was put on:
 * `paperClass` is the class of the surface inside the popper.
 */
export const popperGap = (theme: Theme, paperClass: string) => ({
  [`&[data-popper-placement^="bottom"] .${paperClass}`]: {
    marginTop: theme.spacing(ANCHOR_GAP),
  },
  [`&[data-popper-placement^="top"] .${paperClass}`]: {
    marginBottom: theme.spacing(ANCHOR_GAP),
  },
})

export default function Popover(theme: Theme) {
  return {
    MuiPopover: {
      styleOverrides: {
        // The gap is set from the root because the root is where MUI hands
        // over the origins, and its one paper is the only thing it holds.
        root: ({ ownerState }: { ownerState: PopoverProps }) => ({
          [`& .${popoverClasses.paper}`]: popoverGap(theme, ownerState),
        }),
        paper: {
          boxShadow: popSurfaceShadow(theme),
          borderRadius: theme.radii.surface,
        },
      },
    },
  }
}
